import React, { useEffect, useMemo, useState } from "react";
import Linkedin from "../Atomos/Icons/ViewMode/Linkedin";
import Facebook from "../Atomos/Icons/ViewMode/Facebook";
import Instagram from "../Atomos/Icons/ViewMode/Instagram";
import Whatsapp from "../Atomos/Icons/ViewMode/Whatsapp";
import WeChat from "../Atomos/Icons/ViewMode/WeChat";
import Youtube from "../Atomos/Icons/ViewMode/Youtube";
import XIcon from "../Atomos/Icons/ViewMode/XDark";
import PencilEdit from "../Atomos/Icons/PencilEdit";
import { Link } from "react-router-dom";
import VCard from "../Atomos/Icons/ViewMode/VCard";
import { IPerson } from "../../interfaces/models/IPerson";
import { UseFirmList } from "../../hooks/Catalogs/useFirm";
import Axios from "../../utils/axios";
import { useStaffMode } from "../../hooks/StaffModeProvider";
import { useAuth } from "../../store/AuthContext";
import { toastWarn } from "../../utils/toast";
import { resolvePhoneForVCard } from "../../utils/formatPhoneForDisplay";
const axios = new Axios();

interface ISocialLinks {
  organization?: boolean;
  user?: any;
  organizationObj?: any;
  isEditable?: boolean;
  isPublicPreview?: boolean;
  isDataMissing?: (fields: string[]) => void;
  /** Tailwind classes for the icon row (default centers icons). */
  rowClassName?: string;
  /** Extra control (e.g. edit) after icons/vCard — participates in the same flex-wrap row as the icons. */
  suffix?: React.ReactNode;
  /** Member public profile = red icon hover; staff profile page = blue hover. Defaults to member. */
  profileTheme?: "member" | "staff";
}

const SocialLinks: React.FC<ISocialLinks> = ({
  organization,
  user,
  organizationObj,
  isEditable,
  isPublicPreview,
  isDataMissing,
  rowClassName,
  suffix,
  profileTheme = "member",
}) => {
  const [currentUser, setCurrentUser] = useState<IPerson | null>(null);
  const [personFirm, setPersonFirm] = useState<any>();
  const [personMainOffice, setPersonMainOffice] = useState<any>();
  const { firmData } = UseFirmList();
  const { isStaffMode } = useStaffMode();
  const { isAuthenticated, loggedPerson } = useAuth();

  useEffect(() => {
    setCurrentUser(user || null);
  }, [user]);

  useEffect(() => {
    const userFirm = firmData.find((firm: any) => firm.id === user?.firm);
    setPersonFirm(userFirm);
  }, [user, firmData]);

  const socialNetworks = [
    {
      name: "linkedin",
      userProp: "linkedin",
      orgProp: "linkedinUrl",
      Component: Linkedin,
    },
    { name: "x", userProp: "x", orgProp: "xUrl", Component: XIcon },
    {
      name: "facebook",
      userProp: "facebook",
      orgProp: "facebookUrl",
      Component: Facebook,
    },
    {
      name: "instagram",
      userProp: "instagram",
      orgProp: "instagramUrl",
      Component: Instagram,
    },
    {
      name: "whatsapp",
      userProp: "whatsapp",
      orgProp: "",
      Component: Whatsapp,
    },
    {
      name: "wechat",
      userProp: "wechat",
      orgProp: "wechatUrl",
      Component: WeChat,
    },
    {
      name: "youtube",
      userProp: "youtube",
      orgProp: "youtubeUrl",
      Component: Youtube,
    },
  ];

  const filteredSocialNetworks = organization
    ? socialNetworks.filter(({ orgProp }) => orgProp)
    : socialNetworks.filter(({ name }) => name !== "youtube");

  const findSocialNetworkUrl = (name: string) => {
    if (organization) {
      const network = socialNetworks.find((network) => network.name === name);
      return organizationObj
        ? organizationObj[network ? network?.orgProp : ""]
        : "";
    } else {
      const networks = currentUser?.socialNetworks;
      const network = networks?.find(
        (network: { name?: string; url?: string } | null | undefined) =>
          network?.name != null && String(network.name).toLowerCase() === name,
      );
      return network ? network.url : "";
    }
  };

  const formatUrl = (url: string) => {
    if (url && !url.startsWith("http://") && !url.startsWith("https://")) {
      return `https://${url}`;
    }
    return url;
  };

  const editLinkPath = `/profile/edit/attorney/${user?.id}/${user?.generatedId}`;

  const missingVCardFields = useMemo(() => {
    if (!user || organization) {
      return [];
    }
    const missingFields: string[] = [];
    if (!user.name) {
      missingFields.push("Name");
    }
    if (!user.lastName) {
      missingFields.push("Last Name");
    }
    if (!user.mainOffice) {
      missingFields.push("Main Office");
    }
    if (
      !resolvePhoneForVCard(user?.phoneNumber, personMainOffice?.phoneNumber)
    ) {
      missingFields.push("Phone Number");
    }
    if (!user.firmEmail) {
      missingFields.push("Email");
    }
    return missingFields;
  }, [user, personMainOffice, organization]);

  const vCardReady = missingVCardFields.length === 0;
  const hoverFillClass =
    profileTheme === "staff" ? "hoverFillStaff" : "hoverFillMember";
  const isOwnProfile = Boolean(
    loggedPerson?.id && user?.id && loggedPerson.id === user.id,
  );

  const generateVCard = () => {
    if (missingVCardFields.length > 0) {
      isDataMissing?.(missingVCardFields);
      toastWarn(
        isOwnProfile
          ? "Cannot download vCard — required profile information is missing. Hover the icon to see what's needed."
          : "Cannot download this vCard — some contact details are missing. Hover the icon for details.",
      );
      return;
    }
    if (user) {
      const formattedPhoneNumber = resolvePhoneForVCard(
        user.phoneNumber,
        personMainOffice?.phoneNumber,
      );
      const adrCity =
        personMainOffice?.cityName ?? personMainOffice?.city ?? "";
      const adrState =
        personMainOffice?.stateName ?? personMainOffice?.state ?? "";
      const adrCountry =
        personMainOffice?.countryName ?? personMainOffice?.country ?? "";

      const vCardData = `
BEGIN:VCARD
VERSION:3.0
N:${user.lastName};${user.name};${user.middleInitial ?? ""};${user.honorary || ""};${user.suffix || ""}
ORG:${personFirm && personFirm?.firmName ? personFirm.firmName : ""}
TITLE:${user.title || ""}
FN:${user.displayName || `${user.name} ${user.middleInitial || ""} ${user.lastName}`}
TEL;TYPE=WORK,VOICE:${formattedPhoneNumber}
EMAIL:${user.firmEmail || ""}
ADR;TYPE=WORK:;;${personMainOffice?.address || ""};${adrCity};${adrState};;${adrCountry}
END:VCARD
      `.trim();

      const blob = new Blob([vCardData], { type: "text/vcard" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${user.displayName || user.name}.vcf`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  return (
    <React.Fragment>
      <div
        className={`flex min-w-0 flex-wrap gap-[.625rem] items-center ${rowClassName ?? "justify-center"}`}
      >
        {filteredSocialNetworks?.map(({ name, Component }) => {
          const url = findSocialNetworkUrl(name);
          const formattedUrl = formatUrl(url);
          const iconClass = url ? hoverFillClass : "socialInactive opacity-50";
          return (
            <div
              className={`w-[1.25rem] h-[1.25rem] shrink-0 flex items-center justify-center ${iconClass}`}
              key={name}
            >
              <a
                href={formattedUrl || undefined}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full h-full flex relative"
              >
                <Component />
              </a>
            </div>
          );
        })}
        {(isPublicPreview || !isEditable) &&
          !organization &&
          isAuthenticated && (
            <div
              className={`flex h-[1.25rem] w-[1.25rem] shrink-0 items-center justify-center ${
                vCardReady
                  ? `cursor-pointer ${hoverFillClass}`
                  : "cursor-not-allowed socialInactive opacity-50"
              }`}
              onClick={generateVCard}
              role="button"
              tabIndex={vCardReady ? 0 : -1}
              onKeyDown={(e) => {
                if (!vCardReady) {
                  return;
                }
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  generateVCard();
                }
              }}
              title={
                vCardReady
                  ? "Download vCard"
                  : isOwnProfile
                    ? `Complete your profile to download — missing: ${missingVCardFields.join(", ")}`
                    : `vCard unavailable — missing: ${missingVCardFields.join(", ")}`
              }
              aria-disabled={!vCardReady}
            >
              <VCard />
            </div>
          )}
        {isEditable && !organization && (
          <Link to={editLinkPath} state={{ ...user, isOnlySocials: true }}>
            <div
              className={`w-[1.5rem] h-[1.5rem] flex items-center justify-center ${isStaffMode ? "fill-Default" : "fill-accentDefault"}`}
            >
              <PencilEdit className="w-full h-full" />
            </div>
          </Link>
        )}
        {suffix}
      </div>
    </React.Fragment>
  );
};

export default SocialLinks;
